import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clearStashedAudience, setAudienceEditSource, type AudienceEditSource } from '@/features/audienceEdit/audienceEditSlice';
import { AudienceEditV2 } from '@/features/audienceEdit_v2';
import { showToast } from '@/features/toasts/toastsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useModalContext } from '@/hooks/useModalContext';
import { Audience } from '@/models/audiences';
import { ToastType } from '@/models/toast';
import { useAppDispatch } from '@/reducers';
import { useLazyGetDataSourcesByIdsQuery } from '@/services/dataSources';
import { AUDIENCE_PREFILL_TIMEOUT_MS, useAudiencePrefillMutation } from '@/services/endpoints/audiencePrefill';
import { useDeleteAudienceMutation } from '@/services/endpoints/audiences';
import { useGetWorkspaceQuery, useUpdateWorkspaceAiConsentMutation } from '@/services/endpoints/workspaces';
import { SuggestedAudience } from '../types';

export type SuggestedAudienceCreatePhase = 'idle' | 'generating' | 'modalDs' | 'modal403';

type UseSuggestedAudienceCreateResult = {
  phase: SuggestedAudienceCreatePhase;
  activeAudience: SuggestedAudience | null;
  missingDatasourceName: string | null;
  isEnablingConsent: boolean;
  createFromSuggestion: (audience: SuggestedAudience) => Promise<void>;
  cancelGeneration: () => void;
  closeModals: () => void;
  enableConsent: () => Promise<void>;
};

const getErrorStatus = (error: unknown): number | string | null => {
  if (!error || typeof error !== 'object') {
    return null;
  }

  if ('status' in error) {
    return (error as { status: number | string }).status;
  }

  return null;
};

export const useSuggestedAudienceCreate = (): UseSuggestedAudienceCreateResult => {
  const { t } = useTranslation('marketIntelligence');
  const workspace = useWorkspace();
  const dispatch = useAppDispatch();
  const { openModal } = useModalContext();
  const { data: workspaceDetails } = useGetWorkspaceQuery({ workspaceId: workspace.id });
  const [updateAiConsent, { isLoading: isEnablingConsent }] = useUpdateWorkspaceAiConsentMutation();
  const [fetchDataSourcesByIds] = useLazyGetDataSourcesByIdsQuery();
  const [audiencePrefill] = useAudiencePrefillMutation();
  const [deleteAudience] = useDeleteAudienceMutation();

  const abortControllerRef = useRef<AbortController | null>(null);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledByUserRef = useRef(false);
  const createdAudienceIdRef = useRef<string | null>(null);
  const expressionPersistedRef = useRef(false);
  const inFlightRef = useRef(false);
  const runIdRef = useRef(0);

  const [phase, setPhase] = useState<SuggestedAudienceCreatePhase>('idle');
  const [activeAudience, setActiveAudience] = useState<SuggestedAudience | null>(null);
  const [missingDatasourceName, setMissingDatasourceName] = useState<string | null>(null);

  const clearTimers = useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  }, []);

  const resetWizardPersistenceTracking = useCallback(() => {
    createdAudienceIdRef.current = null;
    expressionPersistedRef.current = false;
  }, []);

  const resetToIdle = useCallback(() => {
    clearTimers();
    abortControllerRef.current = null;
    inFlightRef.current = false;
    setPhase('idle');
    setActiveAudience(null);
    setMissingDatasourceName(null);
  }, [clearTimers]);

  const settleInFlight = useCallback((runId: number) => {
    if (runId !== runIdRef.current) {
      return;
    }
    inFlightRef.current = false;
  }, []);

  const handleAudienceCreated = useCallback((audienceId: string) => {
    createdAudienceIdRef.current = audienceId;
  }, []);

  const handleExpressionPersisted = useCallback(() => {
    expressionPersistedRef.current = true;
  }, []);

  const handleWizardModalClose = useCallback(() => {
    const createdAudienceId = createdAudienceIdRef.current;
    const expressionPersisted = expressionPersistedRef.current;
    resetWizardPersistenceTracking();

    // Prefill keeps expression in the client stash before it is PATCHed; only keep
    // the server record once expression persistence (or later steps) has succeeded.
    if (createdAudienceId && !expressionPersisted) {
      void deleteAudience({
        workspaceId: workspace.id,
        audienceId: createdAudienceId,
      });
    }
  }, [deleteAudience, resetWizardPersistenceTracking, workspace.id]);

  const openWizard = useCallback(
    (initialAudience: Partial<Audience>, source?: AudienceEditSource) => {
      dispatch(clearStashedAudience());
      resetWizardPersistenceTracking();

      if (source === 'prefill') {
        dispatch(setAudienceEditSource(source));
      }

      openModal({
        renderContent: AudienceEditV2,
        renderContentProps: {
          onSuccess: () => {},
          initialAudience,
          onAudienceCreated: handleAudienceCreated,
          onExpressionPersisted: handleExpressionPersisted,
          ...(source ? { source } : {}),
        },
        fullHeight: true,
        fullWidth: true,
        onClose: handleWizardModalClose,
      });
    },
    [dispatch, handleAudienceCreated, handleExpressionPersisted, handleWizardModalClose, openModal, resetWizardPersistenceTracking]
  );

  const cancelGeneration = useCallback(() => {
    cancelledByUserRef.current = true;
    runIdRef.current += 1;
    abortControllerRef.current?.abort();
    resetToIdle();
  }, [resetToIdle]);

  const closeModals = useCallback(() => {
    runIdRef.current += 1;
    resetToIdle();
  }, [resetToIdle]);

  const enableConsent = useCallback(async () => {
    try {
      await updateAiConsent({ workspaceId: workspace.id, granted: true }).unwrap();
      resetToIdle();
    } catch {
      // Keep modal open; workspace query remains unchanged until success.
    }
  }, [resetToIdle, updateAiConsent, workspace.id]);

  const createFromSuggestion = useCallback(
    async (audience: SuggestedAudience) => {
      setActiveAudience(audience);
      setMissingDatasourceName(null);

      if (!(workspaceDetails?.aiUsageConsent ?? false)) {
        setPhase('modal403');
        return;
      }

      if (inFlightRef.current) {
        return;
      }

      inFlightRef.current = true;
      runIdRef.current += 1;
      const runId = runIdRef.current;
      cancelledByUserRef.current = false;
      setPhase('generating');

      const datasourceIds = audience.suggestedDatasourceIds;

      try {
        const existingDataSources = await fetchDataSourcesByIds({
          workspaceId: workspace.id,
          datasourceIds,
        }).unwrap();

        if (runId !== runIdRef.current) {
          return;
        }

        const existingIds = new Set(existingDataSources.map((dataSource) => dataSource.id));
        const missingId = datasourceIds.find((id) => !existingIds.has(id));

        if (missingId || existingDataSources.length < datasourceIds.length) {
          setMissingDatasourceName(missingId ?? datasourceIds[0] ?? null);
          setPhase('modalDs');
          settleInFlight(runId);
          return;
        }
      } catch {
        if (runId !== runIdRef.current) {
          return;
        }

        resetToIdle();
        dispatch(
          showToast({
            type: ToastType.ERROR,
            title: t('report.suggestedAudiences.create.genericError.title'),
            message: t('report.suggestedAudiences.create.genericError.description'),
          })
        );
        return;
      }

      if (runId !== runIdRef.current) {
        return;
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;
      clearTimers();
      timeoutIdRef.current = setTimeout(() => {
        controller.abort();
      }, AUDIENCE_PREFILL_TIMEOUT_MS);

      try {
        const prefill = await audiencePrefill({
          workspaceId: workspace.id,
          action: audience.rawAction,
          signal: controller.signal,
        }).unwrap();

        if (runId !== runIdRef.current || cancelledByUserRef.current || controller.signal.aborted) {
          return;
        }

        resetToIdle();
        openWizard(
          {
            name: prefill.name,
            matchingTrait: prefill.matchingTrait || audience.suggestedMatchingTrait,
            datasourceIds: audience.suggestedDatasourceIds,
            expression: prefill.expression ?? {},
          },
          'prefill'
        );
      } catch (error) {
        if (runId !== runIdRef.current || cancelledByUserRef.current) {
          return;
        }

        const status = getErrorStatus(error);

        if (status === 429) {
          resetToIdle();
          dispatch(
            showToast({
              type: ToastType.WARNING,
              title: t('report.suggestedAudiences.create.rateLimit.title'),
              message: t('report.suggestedAudiences.create.rateLimit.description'),
            })
          );
          return;
        }

        if (status === 403) {
          setPhase('modal403');
          settleInFlight(runId);
          return;
        }

        if (status === 502) {
          resetToIdle();
          openWizard({
            name: audience.title,
            matchingTrait: audience.suggestedMatchingTrait,
            datasourceIds: audience.suggestedDatasourceIds,
          });
          return;
        }

        resetToIdle();
        dispatch(
          showToast({
            type: ToastType.ERROR,
            title: t('report.suggestedAudiences.create.genericError.title'),
            message: t('report.suggestedAudiences.create.genericError.description'),
          })
        );
      } finally {
        if (runId === runIdRef.current) {
          clearTimers();
        }
      }
    },
    [audiencePrefill, clearTimers, dispatch, fetchDataSourcesByIds, openWizard, resetToIdle, settleInFlight, t, workspace.id, workspaceDetails?.aiUsageConsent]
  );

  return {
    phase,
    activeAudience,
    missingDatasourceName,
    isEnablingConsent,
    createFromSuggestion,
    cancelGeneration,
    closeModals,
    enableConsent,
  };
};
